import React from 'react';
import { Link } from 'react-router-dom';
import { useContacts } from '../../contexts/ContactsContext';
import { useLang } from '../../lib/lang';

/**
 * Site-wide footer. Contacts come from the app-root <ContactsProvider>
 * so route changes don't refetch (was causing brief empty-footer
 * flashes between pages). The provider also holds a hard-coded
 * fallback so the footer stays readable even if the API fails.
 *
 * The colour set adapts to the surface it sits on via the `variant`
 * prop: `dark` (default) for pages with a black background, `light`
 * for pages with a bone/greige surface.
 */

const AI_DISCLAIMER = {
    it: "Alcuni contenuti visivi presenti in questo sito sono stati elaborati con l'ausilio di strumenti di intelligenza artificiale generativa.",
    en: 'Some visual content on this website has been enhanced with the aid of generative artificial intelligence tools.',
};

const LEGAL_LABELS = {
    it: { privacy: 'Privacy Policy', cookie: 'Cookie Policy', notes: 'Note Legali' },
    en: { privacy: 'Privacy Policy', cookie: 'Cookie Policy', notes: 'Legal Notice' },
};

export default function Footer({ variant = 'dark' }) {
    const { contacts } = useContacts();
    const lang = useLang();
    const c = contacts || {};
    const legalBase = lang === 'en' ? '/en/legal' : '/legal';
    const labels = LEGAL_LABELS[lang] || LEGAL_LABELS.it;

    return (
        <footer
            className={`site-footer site-footer--${variant}`}
            data-testid="site-footer"
        >
            <div className="footer-grid">
                <div className="footer-block" data-testid="footer-brand">
                    <div className="footer-label">COMPANY</div>
                    <div className="footer-value">
                        {c.company_name || 'ARAIA ALL DESIGN'}
                    </div>
                </div>
                <div className="footer-block" data-testid="footer-address">
                    <div className="footer-label">ADDRESS</div>
                    <div className="footer-value">{c.address || '—'}</div>
                </div>
                <div className="footer-block" data-testid="footer-email">
                    <div className="footer-label">EMAIL</div>
                    <a
                        className="footer-value footer-link"
                        href={c.email ? `mailto:${c.email}` : '#'}
                    >
                        {c.email || '—'}
                    </a>
                </div>
                {/* iter69·e — PHONE definitivamente RIMOSSO dal footer web
                    (cliente request per la 2ª volta). Il campo `phone` non
                    deve MAI apparire nella UI del sito indipendentemente
                    dal valore in DB. Il numero resta nei biglietti da
                    visita PDF (asset statici forniti dal cliente) e nel
                    campo team_members.phone (uso interno). */}
                <div className="footer-block footer-block--socials" data-testid="footer-socials">
                    <div className="footer-label">SOCIAL</div>
                    <div className="footer-socials">
                        {(c.socials || []).map((s, i) => (
                            <a
                                key={`${s.platform}-${i}`}
                                className="footer-link"
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`footer-social-${s.platform.toLowerCase()}`}
                            >
                                {s.platform}
                            </a>
                        ))}
                    </div>
                </div>
            </div>
            {/* iter70 · Legal links row — small, tracking-wide, middot
                separator. Sits above the AI disclaimer and copyright. */}
            <nav className="footer-legal" data-testid="footer-legal" aria-label={lang === 'en' ? 'Legal' : 'Note legali'}>
                <Link
                    to={`${legalBase}/privacy-policy`}
                    className="footer-legal-link"
                    data-testid="footer-legal-privacy"
                >
                    {labels.privacy}
                </Link>
                <span className="footer-legal-sep" aria-hidden="true">·</span>
                <Link
                    to={`${legalBase}/cookie-policy`}
                    className="footer-legal-link"
                    data-testid="footer-legal-cookie"
                >
                    {labels.cookie}
                </Link>
                <span className="footer-legal-sep" aria-hidden="true">·</span>
                <Link
                    to={`${legalBase}/note-legali`}
                    className="footer-legal-link"
                    data-testid="footer-legal-notes"
                >
                    {labels.notes}
                </Link>
            </nav>
            {/* iter70 · Generative-AI disclaimer — small, muted, but
                readable. Below the legal links row and above the
                copyright line. */}
            <p
                className="footer-ai-disclaimer"
                data-testid="footer-ai-disclaimer"
            >
                {AI_DISCLAIMER[lang] || AI_DISCLAIMER.it}
            </p>
            <div className="footer-baseline" data-testid="footer-copyright">
                © {new Date().getFullYear()} ARAIA ALL DESIGN
            </div>
            <div className="footer-signatures" data-testid="footer-signatures">
                <div className="footer-signature footer-signature--cinzia" data-testid="footer-signature-cinzia">
                    <img
                        className="footer-signature-mark"
                        src="/logos/cinzia/mark-white.png"
                        alt="Cinzia Araia"
                        loading="lazy"
                    />
                    <span className="footer-signature-label">CINZIA ARAIA</span>
                </div>
                <span className="footer-signature-sep" aria-hidden="true" />
                <div className="footer-signature footer-signature--alldesign" data-testid="footer-signature-alldesign">
                    <span className="footer-signature-label" data-testid="footer-signature-alldesign-label">
                        {'ARAIA \\\\ DESIGN'}
                    </span>
                </div>
            </div>
        </footer>
    );
}
